import * as React from 'react'

import { DropdownInput, DropdownInputWithSearchTokens, Tooltip, helpIcon, ProgressReportsSidebar, VERTICAL, HORIZONTAL } from '../../../Shared/index'
import { formatDateForInput } from '../../../Shared/components/shared/dateInput'
import FilterScope from '../filterScope'
import { FETCH_ACTION, RESET_ACTION } from '../../shared'

const DIAGNOSTIC_GROWTH_REPORT_PATH = 'diagnostic_growth_report'

const Filters = ({
  availableTimeframes,
  availableSchools,
  availableGrades,
  availableTeachers,
  availableClassrooms,
  originalAllClassrooms,
  applyFilters,
  clearFilters,
  selectedGrades,
  setSelectedGrades,
  hasAdjustedFiltersFromDefault,
  handleSetSelectedTimeframe,
  selectedTimeframe,
  selectedSchools,
  setSelectedSchools,
  selectedTeachers,
  setSelectedTeachers,
  selectedClassrooms,
  setSelectedClassrooms,
  closeMobileFilterMenu,
  showMobileFilterMenu,
  hasAdjustedFiltersSinceLastSubmission,
  customStartDate,
  customEndDate,
  showFilterMenuButton,
  reportType,
  diagnosticIdForStudentCount,
  pusherChannel,
  sidebarOrientation = VERTICAL
}) => {

  const isGrowthDiagnosticReport = reportType === DIAGNOSTIC_GROWTH_REPORT_PATH

  /* this is a piece of state needed for a UI specification requested by PS and Jack where the filter buttons
     container will expand in height only for the Student section of the Growth Reports
  */
  const [applyFilterButtonClicked, setApplyFilterButtonClicked] = React.useState<boolean>(false)
  const [filterScopeAction, setFilterScopeAction] = React.useState<string>('')

  // Track previous teacher selection to detect actual changes vs remounts
  const prevTeacherCountRef = React.useRef<number | null>(null)

  // Handle edge case where classroom filter needs to be reset when all teachers are selected
  // This was previously done during render which caused cascading re-renders and UI issues
  // See: https://www.notion.so/quill/Address-issue-in-the-admin-report-filters-causing-the-Classroom-filter-to-sometimes-end-up-in-an-u-b10b51c217114e67ae937120be73ecfd
  //
  // Important: Only reset classrooms when teachers actually change, not on component remount.
  // In horizontal mode, this component remounts when switching tabs (Overview/Skill/Student),
  // and we don't want to wipe classroom selections just because the user changed tabs.
  React.useEffect(() => {
    const currentTeacherCount = selectedTeachers?.length ?? 0
    const prevTeacherCount = prevTeacherCountRef.current

    // Only reset if teachers actually changed (not on initial mount or remount with same data)
    const teachersChanged = prevTeacherCount !== null && prevTeacherCount !== currentTeacherCount

    if (teachersChanged && currentTeacherCount === availableTeachers?.length && originalAllClassrooms) {
      setSelectedClassrooms(originalAllClassrooms)
    }

    prevTeacherCountRef.current = currentTeacherCount
  }, [selectedTeachers, availableTeachers, originalAllClassrooms, setSelectedClassrooms])

  function effectiveSelectedSchools() {
    return selectedSchools.filter(s => availableSchools.find(as => as.id === s.id))
  }

  function effectiveSelectedTeachers() {
    return selectedTeachers.filter(t => availableTeachers.find(at => at.id === t.id))
  }

  function effectiveSelectedClassrooms() {
    return selectedClassrooms.filter(c => availableClassrooms.find(ac => ac.id === c.id))
  }

  function handleSetFilterScopeAction(value: string) {
    setFilterScopeAction(value)
  }

  function handleSetApplyFilterButtonClicked(value: boolean) {
    setApplyFilterButtonClicked(value)
  }

  function handleApplyFilters() {
    if(isGrowthDiagnosticReport && diagnosticIdForStudentCount) {
      setFilterScopeAction(FETCH_ACTION)
    }
    applyFilters()
  }

  function handleClearFilters() {
    if (isGrowthDiagnosticReport) {
      setApplyFilterButtonClicked(false)
      setFilterScopeAction(RESET_ACTION)
    }
    clearFilters()
  }

  function renderFilterScope() {
    if(!isGrowthDiagnosticReport) { return }
    return(
      <FilterScope
        diagnosticIdForStudentCount={diagnosticIdForStudentCount}
        filterScopeAction={filterScopeAction}
        handleSetApplyFilterButtonClicked={handleSetApplyFilterButtonClicked}
        handleSetFilterScopeAction={handleSetFilterScopeAction}
        hasAdjustedFiltersFromDefault={hasAdjustedFiltersFromDefault}
        pusherChannel={pusherChannel}
        selectedClassrooms={selectedClassrooms}
        selectedGrades={selectedGrades}
        selectedSchools={selectedSchools}
        selectedTeachers={selectedTeachers}
        selectedTimeframe={selectedTimeframe}
      />
    )
  }

  function renderFilterButtons() {
    if (!hasAdjustedFiltersFromDefault && !hasAdjustedFiltersSinceLastSubmission) { return null }

    let applyClassName = "quill-button small contained green focus-on-light"

    applyClassName += hasAdjustedFiltersSinceLastSubmission ? '' : ' disabled'

    return (
      <div className={`filter-buttons-container fixed ${applyFilterButtonClicked ? 'with-count ' : ''} ${isHorizontal ? 'horizontal' : ''}`}>
        {renderFilterScope()}
        <div className="filter-buttons">
          <button className="quill-button small outlined grey focus-on-light" onClick={handleClearFilters} type="button">Clear filters</button>
          <button className={applyClassName} onClick={handleApplyFilters} type="button">Apply filters</button>
        </div>
      </div>
    )
  }

  const timeframeHelperText = customStartDate && customEndDate ? `${formatDateForInput(customStartDate)} - ${formatDateForInput(customEndDate)}` : null

  let classroomsFilter = (
    <div className="disabled-classroom-filter">
      <DropdownInput
        ariaLabel="Classroom"
        className="bordered small"
        disabled={true}
        id="classroom-filter"
        isMulti={true}
        isSearchable={true}
        label="Classroom"
        labelAdjacentElement={
          (<Tooltip
            tooltipText="To filter by classroom, first apply broader filters above"
            tooltipTriggerText={<img alt={helpIcon.alt} src={helpIcon.src} />}
          />)
        }
        options={[]}
        optionType="classroom"
        value={[]}
      />
    </div>
  )

  if (availableClassrooms.length) {
    classroomsFilter = (
      <DropdownInputWithSearchTokens
        ariaLabel="Classroom"
        id="classroom-filter"
        identifier="id"
        label="Classroom"
        onChange={setSelectedClassrooms}
        options={availableClassrooms}
        optionType="classroom"
        value={selectedClassrooms}
        valueToDisplay={effectiveSelectedClassrooms()}
      />
    )
  }

  // Force vertical orientation when mobile overlay is open (not enough space for horizontal)
  const effectiveOrientation = showMobileFilterMenu ? VERTICAL : sidebarOrientation
  const isHorizontal = effectiveOrientation === HORIZONTAL

  return (
    <ProgressReportsSidebar
      ariaLabel="Filter Menu"
      closeMobileSidebar={closeMobileFilterMenu}
      filterButtons={renderFilterButtons()}
      orientation={effectiveOrientation}
      showMobileSidebar={showMobileFilterMenu}
      showSidebarButton={showFilterMenuButton}
    >
      <div className={`sidebar-section filters ${isHorizontal ? 'horizontal' : ''}`} data-testid="filter-menu">
        <div className="dropdowns">
          <div className="dropdown-wrapper">
            <DropdownInput
              ariaLabel="timeframe"
              className="bordered small focus-on-light"
              handleChange={handleSetSelectedTimeframe}
              helperText={timeframeHelperText}
              id="timeframe-filter"
              isSearchable={false}
              label="Timeframe"
              labelAdjacentElement={
                (<Tooltip
                  tooltipText={isGrowthDiagnosticReport ? "To ensure meaningful results, this report must be viewed for an entire school year, defined as July 1 through June 30. This report is updated nightly." : "This report is updated nightly."}
                  tooltipTriggerText={<img alt={helpIcon.alt} src={helpIcon.src} />}
                />)
              }
              options={availableTimeframes}
              value={selectedTimeframe}
            />
          </div>
          <div className="dropdown-wrapper">
            <DropdownInputWithSearchTokens
              ariaLabel="School"
              id="school-filter"
              identifier="id"
              label="School"
              onChange={setSelectedSchools}
              options={availableSchools}
              optionType="school"
              value={selectedSchools}
              valueToDisplay={effectiveSelectedSchools()}
            />
          </div>
          <div className="dropdown-wrapper">
            <DropdownInputWithSearchTokens
              ariaLabel="Grade"
              id="grade-filter"
              identifier="value"
              label="Grade"
              onChange={setSelectedGrades}
              options={availableGrades}
              optionType="grade"
              value={selectedGrades}
              valueToDisplay={selectedGrades}
            />
          </div>
          <div className="dropdown-wrapper">
            <DropdownInputWithSearchTokens
              ariaLabel="Teacher"
              id="teacher-filter"
              identifier="id"
              label="Teacher"
              onChange={setSelectedTeachers}
              options={availableTeachers}
              optionType="teacher"
              value={selectedTeachers}
              valueToDisplay={effectiveSelectedTeachers()}
            />
          </div>
          <div className="dropdown-wrapper">
            {classroomsFilter}
          </div>
        </div>
      </div>
    </ProgressReportsSidebar>
  )
}

export default Filters
